import { rest } from 'msw';

import {
  DisplaySettings,
  defaultDisplaySettings,
} from '../types/DisplaySettings';
import { SensorDevice } from '../types/SensorDevice';
import { SensorStatus } from '../types/SensorStatus';
import { User } from '../types/User';
import { UserRole } from '../types/UserRole';

// Seed/mock mode is opt-in. When off, the worker leaves the real API alone and
// requests pass through to the dev backend (the normal `yarn start` behavior).
export const isMockSeedEnabled = process.env.REACT_APP_USE_MOCKS === 'true';

const customStatuses = ['휴가중'];

type Seed = {
  name: string;
  position: string;
  status: SensorStatus | string | null;
  memo?: string;
};

const seed: Seed[] = [
  { name: '김형대', position: '의장', status: SensorStatus.OUT },
  { name: '전인수', position: '부의장', status: SensorStatus.OUT },
  {
    name: '한윤수',
    position: '위원장',
    status: SensorStatus.IN,
    memo: '오후 2시 본회의장 이동 예정',
  },
  { name: '이동호', position: '부위원장', status: SensorStatus.OUT },
  { name: '복진경', position: '위원장', status: SensorStatus.OUT },
  {
    name: '우종혁',
    position: '부위원장',
    status: SensorStatus.MEETING,
    memo: '예산결산특위 회의 중',
  },
  { name: '황영각', position: '위원장', status: SensorStatus.OUT },
  { name: '이성수', position: '부위원장', status: SensorStatus.MEETING },
  { name: '오온누리', position: '위원장', status: '휴가중' },
  { name: '손민기', position: '부위원장', status: SensorStatus.OUT },
  { name: '강을석', position: '의원', status: SensorStatus.OUT },
  { name: '김광심', position: '의원', status: SensorStatus.OUT },
  { name: '김현우', position: '의원', status: SensorStatus.OUT },
  { name: '김진경', position: '의원', status: SensorStatus.IN },
  { name: '김현정', position: '의원', status: SensorStatus.OUT },
];

// Persist mock state in localStorage so edits behave like a real backend and
// survive a full page reload (otherwise in-memory state resets to defaults on
// every load and changes look like they "didn't save").
const SETTINGS_KEY = '@@mock-display-settings@@';
const DEVICES_KEY = '@@mock-devices@@';

const load = <T>(key: string, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T;
  } catch {
    /* ignore */
  }
  return fallback;
};
const persist = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

const seedDevices: SensorDevice[] = seed.map((s, i) => ({
  id: `seed-${i + 1}`,
  auto: s.status === SensorStatus.IN || s.status === SensorStatus.OUT,
  status: s.status as SensorDevice['status'],
  companyUser: { id: `user-${i + 1}`, name: s.name, position: s.position },
  memo: s.memo ?? null,
}));

const devices: SensorDevice[] = load(DEVICES_KEY, seedDevices);

let currentSettings: DisplaySettings = {
  ...defaultDisplaySettings,
  ...load(SETTINGS_KEY, {} as Partial<DisplaySettings>),
};

const seedUser: User = {
  id: 'seed-admin',
  login: 'seed-admin',
  phoneNumber: '',
  companyId: 'seed-company',
  firstName: '관리자',
  lastName: '노크',
  activated: true,
  authorities: [{ name: UserRole.ROLE_ADMIN }],
  lastLoggedInAt: new Date().toISOString(),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

const buildHome = () => ({
  companyLogo: '',
  companyName: '강남구의회',
  updatedAt: new Date().toISOString(),
  customStatuses,
  displaySettings: currentSettings,
  sensorDevices: devices,
});

// Wildcard host prefix (`*`) so these match whether the base URL is same-origin
// (local proxy) or an absolute dev/prod domain.
export const seedHandlers = [
  rest.get('*/api/account', (_req, res, ctx) => res(ctx.json(seedUser))),
  rest.get('*/api/home', (_req, res, ctx) => res(ctx.json(buildHome()))),
  rest.get('*/api/sensorDeviceHistories', (_req, res, ctx) =>
    res(ctx.json([])),
  ),
  rest.get('*/api/sensorDeviceHistories/*', (_req, res, ctx) =>
    res(ctx.json([])),
  ),
  rest.put('*/api/sensorDevices/:id', async (req, res, ctx) => {
    const body = (await req.json().catch(() => ({}))) as Partial<SensorDevice>;
    const device = devices.find((d) => d.id === req.params.id);
    if (device) {
      if (body.status !== undefined)
        device.status = body.status as SensorDevice['status'];
      if (body.auto !== undefined) device.auto = body.auto;
      if (body.memo !== undefined) device.memo = body.memo;
      persist(DEVICES_KEY, devices);
    }
    return res(ctx.json(device ?? body));
  }),
  rest.put('*/api/admin/display-settings', async (req, res, ctx) => {
    const body = (await req
      .json()
      .catch(() => ({}))) as Partial<DisplaySettings>;
    currentSettings = { ...currentSettings, ...body };
    persist(SETTINGS_KEY, currentSettings);
    return res(ctx.json(currentSettings));
  }),
  // Hold the SSE connection open without responding so EventSourcePolyfill
  // doesn't enter a reconnect loop in mock mode (no live backend to stream).
  rest.get('*/api/events', (_req, res, ctx) => res(ctx.delay('infinite'))),
];
